import { useContext, useEffect, useState } from "react";
import CompanyService, { DataType } from "../../services/CompanyService";
import { FilterCollection } from "../../services/FilterService";
import InvoiceService from "../../services/InvoiceService";
import {
  formatCurrency,
  formatNumber,
  formatDateShort
} from "../../utils/FormatUtils";
import { parseDate, restTimezone, MapIfHasHours } from "../../utils/ParseUtils";
import { OptionalMap } from "../../utils/Utils";
import { InvoiceStatusHistoryList } from "../client/invoice/entities/InvoiceStatusHistoryListResponse";
import { TableColumn } from "../client/invoice/InvoiceTableItem";
import InvoiceStatusTableProvider, {
  InvoiceStatusTableContextValues
} from "../client/invoice/InvoiceStatusTableProvider";
import InvoiceTableTotal from "../client/invoice/InvoiceTableTotal";
import AdvancedFilters, {
  AdvancedFiltersButton,
  TYPE_REPORT
} from "../shared/components/AdvancedFilters";
import Table, { TableHeaderWithFieldId } from "../shared/Table";
import { Link, useLocation } from "react-router-dom";
import TableContext from "../task/TableContext";
import ClientService from "../../services/ClientService";
import { TranslationService } from "../../services/TranslationService";
import { ButtonTooltipIcon } from "../shared/components/ButtonTooltipIcon";
import { ActivityContentTableData } from "./ReportPayment";
import InvoiceDetail from "../client/invoice/InvoiceDetail";
import FloatingPanelService from "../shared/FloatingPanel";
import Dropdown from "../shared/components/Dropdown";
import SearchInput from "../shared/components/SearchInput";
import { CacheEntity, StorageService } from "../../services/StorageService";
import "../client/invoice/invoiceStyles.css";
import { handleErrorWithToast } from "../../utils/RequestUtils";

const ReportInvoiceStatusList = () => {
  const tableHeaders = getTableHeaders();
  const { search } = useLocation();
  const urlParams = new URLSearchParams(search);
  const filterQS = urlParams.get("filter")
    ? [...urlParams.entries()]
        .map(
          (x, i) => "filter" + i.toString() + "=" + x[1].replace("filter0=", "")
        )
        .join("&")
    : "";
  const impid = urlParams.get("impid");
  const [quickFilter, setQuickFilter] = useState<number | undefined>(1);

  const invoiceFilters = [
    { value: 1, text: TranslationService.translate.PendingInvoices },
    { value: 0, text: TranslationService.translate.DueInvoices },
    { value: 4, text: TranslationService.translate.ClaimableInvoices },
    { value: 7, text: TranslationService.translate.PaidInvoices },
    { value: 2, text: TranslationService.translate.AllInvoices }
  ];

  const onChangeQuickFilter = (value: number) => {
    setQuickFilter(value);
  };

  return (
    <InvoiceStatusTableProvider
      pagesize={50}
      extraFilters={filterQS}
      quickfilter={quickFilter?.toString() ?? ""}
      impid={impid}
    >
      <div className="container-fluid padding">
        <div className="d-flex justify-content-between">
          <h2>{TranslationService.translate.InvoiceStatusList}</h2>
        </div>
        <div className="mh-100 p-0">
          <div className="genericHeader">
            <div className="searcherFilterHeader">
              <ExportButton />
              <AdvancedFiltersButton savedFiltersId={TYPE_REPORT["IO"]} />
              <div className="d-flex align-items-center">
                <p className="text-granite-gray me-3">
                  {TranslationService.translate.Status}:
                </p>
                <Dropdown
                  quickFilter
                  defaultValue={quickFilter}
                  onChange={onChangeQuickFilter}
                  items={invoiceFilters}
                />
              </div>
            </div>
            <Search />
          </div>
          <div className="pt-0">
            <AdvancedFilters
              page={FilterCollection.ReportInvoice}
              defaultValue={filterQS}
            />
          </div>
          <Table
            headers={tableHeaders}
            item={ReportInvoiceStatusListItem}
            stickyHeader={true}
            fillHeight={true}
          >
            <InvoiceTableTotal tableHeaders={tableHeaders} />
          </Table>
        </div>
      </div>
    </InvoiceStatusTableProvider>
  );
};

export const ExportButton = () => {
  const { getFilters, response } = useContext(
    TableContext
  ) as InvoiceStatusTableContextValues;
  const exportInvoices = () => {
    InvoiceService.exportIoStatusList(getFilters(), response!.itemCount);
  };
  return (
    <div className="actionButtonsHeader" style={{ marginTop: 1 }}>
      {CompanyService.canDo("export") && (
        <ButtonTooltipIcon
          title={TranslationService.translate.Export}
          icon="fa-light fa-arrow-down-to-line"
          onClick={exportInvoices}
        />
      )}
    </div>
  );
};

export const ReportInvoiceStatusListItem = ({
  data
}: {
  data: InvoiceStatusHistoryList.Item;
}) => {
  const tableHeaders = getTableHeaders();
  const clientFieldIds = ClientService.getClientHeaders().map((x) => x.fieldId);
  const specialFields = {
    "last-activity": (x: InvoiceStatusHistoryList.Item) =>
      ActivityContentTableData({ Activity: x.LastLogMessage } as Parameters<
        typeof ActivityContentTableData
      >[0]),
    referenceNumber: (x: InvoiceStatusHistoryList.Item) => (
      <td className="is-link">
        <span
          className="w-md pointer txt-blue"
          onClick={() =>
            FloatingPanelService.showPanel({
              children: <InvoiceDetail invoiceId={x.IOID} reload={() => {}} />,
              title: TranslationService.translate.Invoice,
              width: 750,
              height: 800
            })
          }
        >
          {data.referenceNumber}
        </span>
      </td>
    ),
    status: (item: InvoiceStatusHistoryList.Item) => (
      <td>
        {CompanyService.getIoStatus().find(
          (x) => x.IOStatusID === item.statusChangeStatusId
        )?.Value ?? CompanyService.getNormalIOStatusName()}
      </td>
    ),
    group: function GroupFieldImpl(item: InvoiceStatusHistoryList.Item) {
      useEffect(() => {
        if (item.Group) {
          StorageService.setCache<string>(
            CacheEntity.GroupName,
            item.Group.GroupID.toString(),
            item.Group.Name
          );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);
      return <td>{item.Group ? item.Group.Name : ""}</td>;
    },
    download: () =>
      !data.HasFiles ? (
        <td></td>
      ) : (
        <td>
          <i
            className="fas fa-fw fa-download pointer"
            onClick={(e) => {
              e.stopPropagation();
              handleErrorWithToast(
                InvoiceService.downloadAllFilesFromInvoice(data.IOID)
              );
            }}
          />
        </td>
      )
  };

  return (
    <tr key={data.IOID}>
      {tableHeaders.map((header) =>
        clientFieldIds.includes(header.fieldId) ? (
          <ClientColumn key={header.fieldId} data={data} header={header} />
        ) : (
          <TableColumn
            key={header.fieldId}
            data={data}
            header={header}
            specialCases={specialFields}
          />
        )
      )}
    </tr>
  );
};

const ClientColumn = ({
  data,
  header
}: {
  data: InvoiceStatusHistoryList.Item;
  header: TableHeaderWithFieldId;
}) => {
  const { currentLanguage } = TranslationService;
  const getValue = () => {
    if (header.key.startsWith("ioadditional.")) {
      return data.Person.additionals.find(
        (x) => x.Id.toString() === header.fieldId
      )?.Value;
    }
    return data.Person[
      header.fieldId as keyof InvoiceStatusHistoryList.Item["Person"]
    ];
  };
  const value = getValue();
  switch (header.fieldId) {
    case "Name":
      return (
        <td className="is-link th-truncate">
          <span className="w-md">
            <Link to={`/${currentLanguage}/client/details?id=${data.personId}`}>
              {data.Person.Name}
            </Link>
          </span>
        </td>
      );
  }
  if (value === undefined || value === null || value === "") {
    return <td></td>;
  }
  switch (header.type) {
    case DataType.Currency:
      return (
        <td className="text-end th-truncate">
          {formatCurrency(parseFloat(value), data.CurrencyID)}
        </td>
      );
    case DataType.Number:
      return <td className="text-end th-truncate">{formatNumber(value)}</td>;
    case DataType.Date:
      return (
        <td className="th-truncate">
          {OptionalMap(parseDate(value), (x) =>
            formatDateShort(MapIfHasHours(x, restTimezone))
          )}
        </td>
      );
    case DataType.List: {
      const additionalId = data.Person.additionals.find(
        (x) => x.Id.toString() === header.fieldId
      )?.Id;
      const additionalDefinition =
        CompanyService.getAdditionalDefinitions().find(
          (x) =>
            x.AdditionalDefinitionID.toString() === additionalId?.toString()
        );
      const definition = additionalDefinition?.AdditionalDefinitionItems.find(
        (x) => x.AdditionalDefinitionItemID.toString() === value
      );
      return <td className="th-truncate">{definition?.Value}</td>;
    }
    default:
      return <td className="th-truncate">{value}</td>;
  }
};

function getTableHeaders() {
  const tableHeaders = InvoiceService.getTableHeaders();
  const idxStatus = tableHeaders.findIndex((x) => x.fieldId === "status");
  if (idxStatus >= 0) {
    const status = { ...tableHeaders[idxStatus] };
    status.numeric = false;
    tableHeaders.splice(idxStatus, 1, status);
  }

  const clientHeaders = ClientService.getClientHeaders();

  const sortedClientHeaders = clientHeaders;

  // Agrandar anchos de las columnas de clientes
  sortedClientHeaders.forEach((header) => {
    if (header.fieldId === "Name") {
      header.className =
        (header.className || "").replace(/long-text/g, "").trim() +
        " long-text w-300px th-truncate";
    } else if (!header.className || header.className.trim() === "") {
      header.className = "w-200px th-truncate";
    } else {
      if (!header.className.includes("w-")) {
        header.className = header.className.trim() + " w-200px";
      }
      if (!header.className.includes("th-truncate")) {
        header.className = header.className.trim() + " th-truncate";
      }
    }
  });

  if (sortedClientHeaders.find((x) => x.fieldId === "Name") === undefined) {
    sortedClientHeaders.unshift(
      new TableHeaderWithFieldId(
        "client",
        TranslationService.translate.Client,
        false,
        true,
        "Name",
        DataType.Client,
        "w-300px th-truncate"
      )
    );
  }

  const statusChangeHeaders = [
    new TableHeaderWithFieldId(
      "statuschangedate",
      TranslationService.translate.StatusChangeDate,
      false,
      true,
      "statusChangeDate",
      DataType.Date,
      "th-w-160 th-nowrap"
    ),
    new TableHeaderWithFieldId(
      "statusChangeUserName",
      TranslationService.translate.StatusChangeUser,
      false,
      false,
      "statusChangeUserName",
      DataType.Text,
      "th-min-200 th-max-600 th-truncate"
    )
  ];

  tableHeaders.unshift(...statusChangeHeaders);
  tableHeaders.unshift(...sortedClientHeaders);

  tableHeaders.push(
    new TableHeaderWithFieldId(
      "last-activity",
      TranslationService.translate.LastActivity,
      false,
      false,
      "last-activity",
      DataType.ActivityType,
      "th-min-200 th-max-600 th-truncate"
    )
  );

  tableHeaders.push({
    fieldId: "download",
    key: "donwload",
    className: "th-w-40 th-center",
    label: () => <></>,
    numeric: false,
    sorteable: false,
    tooltip: "",
    type: DataType.Download
  });

  return tableHeaders;
}

const Search = () => {
  const { applySearch } = useContext(TableContext);
  return (
    <div className="searcherFilterHeader" style={{ height: 40 }}>
      <SearchInput onSearch={applySearch} autosearch={false} />
    </div>
  );
};

export default ReportInvoiceStatusList;
